import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { surveyAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Star, MessageSquare, Loader2, CheckCircle, Clock,
  Building2, User, ChevronRight, SkipForward,
} from 'lucide-react';

function StarRating({ value, onChange, size = 'lg' }) {
  const [hover, setHover] = useState(0);
  const sizeClass = size === 'lg' ? 'h-8 w-8' : 'h-5 w-5';
  return (
    <div className="flex gap-1" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          className="focus:outline-none transition-transform hover:scale-110"
          data-testid={`star-${star}`}
        >
          <Star
            className={`${sizeClass} transition-colors ${
              star <= (hover || value)
                ? 'fill-amber-400 text-amber-400'
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export default function CustomerSurveys() {
  const [surveys, setSurveys] = useState([]);
  const [measures, setMeasures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSurvey, setActiveSurvey] = useState(null);
  const [ratings, setRatings] = useState({});
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);

  const loadSurveys = useCallback(async () => {
    setLoading(true);
    try {
      const res = await surveyAPI.getPending();
      setSurveys(res.data.surveys || []);
      setMeasures(res.data.measures || []);
    } catch (err) {
      toast.error('Failed to load surveys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSurveys(); }, [loadSurveys]);

  const openSurvey = (survey) => {
    setActiveSurvey(survey);
    setRatings({});
    setComments('');
    setCompleted(false);
  };

  const handleSubmit = async () => {
    if (!activeSurvey) return;
    const missing = measures.filter(m => !ratings[m.key]);
    if (missing.length > 0) {
      toast.error(`Please rate all categories: ${missing.map(m => m.label).join(', ')}`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await surveyAPI.submit(activeSurvey.id, { ratings, comments: comments || null });
      setCompleted(true);
      toast.success('Thank you for your feedback!');
      setTimeout(() => {
        setActiveSurvey(null);
        setCompleted(false);
        loadSurveys();
      }, 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit survey');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async (surveyId) => {
    try {
      await surveyAPI.skip(surveyId);
      toast.info('Survey skipped');
      loadSurveys();
    } catch {
      toast.error('Failed to skip survey');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  // Active survey form
  if (activeSurvey && !completed) {
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="survey-form">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Rate Your Experience</h1>
          <p className="text-gray-500 mt-1">
            Your feedback helps us improve our services
          </p>
        </div>

        <Card className="border-teal-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-1">
              <User className="h-5 w-5 text-teal-600" />
              <span className="font-medium text-gray-900">{activeSurvey.agent?.full_name || 'Agent'}</span>
              {activeSurvey.agent?.agent_code && (
                <Badge className="bg-teal-100 text-teal-700 text-xs">{activeSurvey.agent.agent_code}</Badge>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Building2 className="h-4 w-4" />
              <span>{activeSurvey.property?.name || 'Property Viewing'}</span>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-5">
          {measures.map(measure => (
            <Card key={measure.key} className="border" data-testid={`measure-${measure.key}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex-1 pr-4">
                    <p className="font-medium text-gray-900">{measure.label}</p>
                    <p className="text-sm text-gray-500 mt-0.5">{measure.description}</p>
                  </div>
                  <StarRating
                    value={ratings[measure.key] || 0}
                    onChange={v => setRatings(prev => ({ ...prev, [measure.key]: v }))}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="border">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="h-5 w-5 text-gray-400" />
              <p className="font-medium text-gray-900">Additional Comments</p>
              <Badge className="bg-gray-100 text-gray-500 text-xs">Optional</Badge>
            </div>
            <Textarea
              value={comments}
              onChange={e => setComments(e.target.value)}
              placeholder="Share any additional thoughts about your experience..."
              rows={3}
              data-testid="survey-comments"
            />
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => setActiveSurvey(null)}
            data-testid="survey-back-btn"
          >
            Back
          </Button>
          <Button
            className="flex-1 bg-teal-600 hover:bg-teal-700 gap-2"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="submit-survey-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
            Submit Feedback
          </Button>
        </div>
      </div>
    );
  }

  // Completion animation
  if (activeSurvey && completed) {
    return (
      <div className="max-w-md mx-auto text-center py-20" data-testid="survey-complete">
        <CheckCircle className="h-20 w-20 text-teal-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
        <p className="text-gray-500">Your feedback has been submitted successfully.</p>
      </div>
    );
  }

  // Survey list
  return (
    <div className="space-y-6" data-testid="customer-surveys-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Star className="h-7 w-7 text-teal-600" />
          My Surveys
        </h1>
        <p className="text-sm text-gray-500 mt-1">Rate your property viewing experiences</p>
      </div>

      {surveys.length === 0 ? (
        <div className="text-center py-20" data-testid="no-surveys">
          <CheckCircle className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-600">No pending surveys</h3>
          <p className="text-sm text-gray-400 mt-1">Surveys will appear here after property viewings</p>
        </div>
      ) : (
        <div className="space-y-3">
          {surveys.map(survey => (
            <Card key={survey.id} className="border hover:shadow-md transition-shadow cursor-pointer" data-testid={`survey-card-${survey.id}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-amber-50 flex items-center justify-center">
                      <Star className="h-6 w-6 text-amber-500" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        Rate {survey.agent?.full_name || 'your agent'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {survey.property?.name || 'Property'} viewing
                      </p>
                      <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                        <Clock className="h-3 w-3" />
                        {survey.created_at ? new Date(survey.created_at).toLocaleDateString() : ''}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-gray-400 hover:text-gray-600"
                      onClick={(e) => { e.stopPropagation(); handleSkip(survey.id); }}
                      data-testid={`skip-survey-${survey.id}`}
                    >
                      <SkipForward className="h-4 w-4 mr-1" /> Skip
                    </Button>
                    <Button
                      size="sm"
                      className="bg-teal-600 hover:bg-teal-700 gap-1"
                      onClick={() => openSurvey(survey)}
                      data-testid={`rate-btn-${survey.id}`}
                    >
                      Rate Now <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
